import React, { useEffect, useState } from 'react';
import ProjectCarousel from '../components/ProjectCarousel';
import GalleryCarousel from '../components/GalleryCarousel';
import Footer from '../components/Footer';
import SecretButton from '../components/SecretButton';
import WorkflowOverlay from '../components/WorkflowOverlay';
import { galleryItems } from '../sources/galleryItems';
import { projectItems } from '../sources/projectItems';
import resumePDF from '../sources/Prahinog, Jake - Resume.pdf';
import '../styles/section-label.css';

const HomePage = () => {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Function to randomly select items
  const getRandomItems = (array, count) => {
    const shuffled = [...array].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Randomly select 6 gallery items for carousel
  const randomGalleryItems = getRandomItems(galleryItems, 6);
  // Randomly select one more for the cover
  const randomCoverItem = galleryItems[Math.floor(Math.random() * galleryItems.length)];

  useEffect(() => {
    // Smooth scrolling for hash links
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          const offsetTop = target.offsetTop - 100;
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth',
          });
        }
      });
    });

    // Check if user is at bottom of page
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Show button when user is within 100px of the bottom
      const atBottom = windowHeight + scrollTop >= documentHeight - 100;
      setIsAtBottom(atBottom);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="container-fluid" style={{ paddingTop: '80px' }}>
      <div className="bento-grid">
        {/* Hero Section */}
        <div className="bento-item hero-section" data-aos="fade-up" id="home">
          <div className="accent-line"></div>
          <h1>Jake Prahinog</h1>
          <p className="lead mb-4" style={{ fontSize: '1.25rem', color: 'rgba(255, 255, 255, 0.7)' }}>
            Web Developer specializing in Backend Development, exploring AI Systems, Automation & Scalable Web Applications
          </p>
          <p className="text-muted mb-4">
            Based in Toledo City, Cebu, Philippines · Building intelligent solutions with modern technologies
          </p>
          <div className="d-flex gap-3 flex-wrap">
            <a href="#contact" className="btn-minimal btn-primary-minimal">Get In Touch</a>
            <a href={resumePDF} download="Jake_Prahinog_Resume.pdf" className="btn-minimal">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
              </svg>
              Download Resume
            </a>
            <a href="#projects" className="btn-minimal">View Projects</a>
            <a href="mailto:prahinogjake@gmail.com" className="btn-minimal">Email Me</a>
          </div>
        </div>

        {/* About Section */}
        <div className="bento-item about-section" data-aos="fade-up" data-aos-delay="100" id="about">
          <div className="accent-line"></div>
          <h2>About Me</h2>
          <p className="text-accent mb-3" style={{ textAlign: 'justify' }}>
            I’m a passionate full-stack developer with a strong focus on backend development, currently exploring AI workflows and intelligent automation. I earned my Bachelor of Science in Information Technology from Cebu Institute of Technology – University (2020–2024), building a solid foundation in system design, scalable architectures, and practical problem-solving. My experience includes developing resilient backend systems, scalable RESTful APIs serving thousands of users, and distributed CI/CD pipelines that accelerate delivery and improve reliability.
          </p>
          <p className="text-accent mb-4" style={{ textAlign: 'justify' }}>
            Recently, I’ve been leveraging LLMs and Retrieval-Augmented Generation (RAG) to design AI-driven automation workflows that reduce manual work, improve efficiency, and enhance system intelligence. Through these initiatives, I’ve helped cut CI feedback time by 50% and reduce support workload by 30–40%, turning complex problems into elegant, production-ready solutions. I thrive at the intersection of backend systems, AI orchestration, and automation, building solutions that scale and make a tangible impact.
          </p>
          <div className="d-flex gap-2 flex-wrap">
            <span className="badge-minimal">Backend Specialist</span>
            <span className="badge-minimal">AI Integration</span>
            <span className="badge-minimal">AI Agent Architect</span>
            <span className="badge-minimal">Workflow Automation</span>
          </div>
        </div>

        {/* Skills Section */}
        <div className="bento-item skills-section" data-aos="fade-up" data-aos-delay="150" id="skills">
          <div className="accent-line"></div>
          <h2>Skills</h2>
          <div className="skill-category">
            <div className="skill-category-title">Backend</div>
            <div className="d-flex flex-wrap gap-2">
              <span className="badge-minimal">PHP</span>
              <span className="badge-minimal">CakePHP</span>
              <span className="badge-minimal">Java</span>
              <span className="badge-minimal">Spring Boot</span>
              <span className="badge-minimal">REST APIs</span>
              <span className="badge-minimal">MySQL</span>
            </div>
          </div>
          <div className="skill-category">
            <div className="skill-category-title">Frontend</div>
            <div className="d-flex flex-wrap gap-2">
              <span className="badge-minimal">Vue.js</span>
              <span className="badge-minimal">React.js</span>
              <span className="badge-minimal">AngularJS</span>
              <span className="badge-minimal">Bootstrap</span>
              <span className="badge-minimal">HTML5</span>
              <span className="badge-minimal">CSS3</span>
            </div>
          </div>
          <div className="skill-category">
            <div className="skill-category-title">AI & DevOps</div>
            <div className="d-flex flex-wrap gap-2">
              <span className="badge-minimal">Prompt Engineering</span>
              <span className="badge-minimal">LLM Integration</span>
              <span className="badge-minimal">RAG</span>
              <span className="badge-minimal">n8n</span>
              <span className="badge-minimal">GitHub Actions</span>
              <span className="badge-minimal">Docker</span>
              <span className="badge-minimal">AWS</span>
            </div>
          </div>
        </div>

        {/* Experience Section Label */}
        <div className="bento-item section-label" data-aos="fade-up" data-aos-delay="180" id="experience">
          <h2 className="mb-0">Experience</h2>
        </div>

        {/* Experience Timeline */}
        <div className="bento-item" style={{ gridColumn: 'span 12' }} data-aos="fade-up" data-aos-delay="200">
          <div className="accent-line"></div>
          <div className="experience-timeline">
            {/* Timeline Item 1 */}
            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-role">Web Developer / Automation Engineer</h3>
                    <p className="timeline-company">Forty Degrees Celsius, Inc.</p>
                  </div>
                  <span className="timeline-date">06/2024 - Present</span>
                </div>
                <div className="timeline-highlights">
                  <span className="timeline-tag">REST APIs • CakePHP</span>
                  <span className="timeline-tag">CI/CD • 50% faster feedback</span>
                  <span className="timeline-tag">AI Workflows • LLMs & RAG</span>
                  <span className="timeline-tag">30-40% support reduction</span>
                </div>
                <div className="timeline-tech">
                  <span className="tech-badge">PHP</span>
                  <span className="tech-badge">CakePHP</span>
                  <span className="tech-badge">Docker</span>
                  <span className="tech-badge">GitHub Actions</span>
                  <span className="tech-badge">LLMs</span>
                  <span className="tech-badge">RAG</span>
                </div>
              </div>
            </div>

            {/* Timeline Item 2 */}
            <div className="timeline-item">
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <div className="timeline-header">
                  <div>
                    <h3 className="timeline-role">Backend Developer Intern</h3>
                    <p className="timeline-company">Pixel8 Web Solutions & Consultancy</p>
                  </div>
                  <span className="timeline-date">01/2024 - 04/2024</span>
                </div>
                <div className="timeline-highlights">
                  <span className="timeline-tag">Backend APIs</span>
                  <span className="timeline-tag">PHPUnit • Codeception</span>
                  <span className="timeline-tag">Vue.js • AngularJS</span>
                  <span className="timeline-tag">Agile • Git workflows</span>
                </div>
                <div className="timeline-tech">
                  <span className="tech-badge">PHP</span>
                  <span className="tech-badge">Vue.js</span>
                  <span className="tech-badge">AngularJS</span>
                  <span className="tech-badge">PHPUnit</span>
                  <span className="tech-badge">Git</span>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Projects Section */}
        <div className="bento-item section-label" data-aos="fade-up" data-aos-delay="280" id="projects">
          <h2 className="mb-0">Works & Projects</h2>
        </div>

        <div style={{ gridColumn: 'span 12' }} data-aos="fade-up" data-aos-delay="300">
          <ProjectCarousel 
            projects={projectItems} 
            onProjectClick={(project) => setSelectedProject(project)} 
          />
        </div>

        {/* AI Gallery Section - Moved */}
        <div className="bento-item" style={{ gridColumn: 'span 12', minHeight: '400px', position: 'relative', overflow: 'hidden' }} data-aos="fade-up" data-aos-delay="480">
          <div className="accent-line"></div>
          <h2 className="mb-3">AI Gallery</h2>
          <p className="text-accent mb-4" style={{ fontSize: '0.95rem' }}>
            Explore my collection of AI-generated images and creative explorations.
          </p>
          <GalleryCarousel 
            items={randomGalleryItems} 
            seeMoreLink="/gallery" 
            seeMoreCover={randomCoverItem.src}
          />
          
          <div style={{ position: 'absolute', top: '2rem', right: '2rem', width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%', animation: 'pulse-dot 2s ease-in-out infinite' }}></div>
        </div>
      </div>

      {/* Footer with Contact Section */}
      <Footer />
      
      {/* Secret Button - only render when at bottom of page */}
      {isAtBottom && <SecretButton />}

      {/* Project Details Overlay */}
      {selectedProject && (
        <WorkflowOverlay 
          project={selectedProject}
          onClose={() => setSelectedProject(null)} 
        />
      )}
    </div>
  );
};

export default HomePage;